/**
 * Runtime configuration types for the application
 */

export interface ApiConfig {
  baseUrl: string;
  loginPath: string;
  useProxy: boolean;
  proxyPath: string;
}

export interface FeaturesConfig {
  enableOfflineMode: boolean;
  enableDebugLogging: boolean;
  // (2026-07-28) Max number of manual-checkout signature images retained
  // on-device (IndexedDB) as a recovery safety net. Oldest are evicted once
  // the count exceeds this. Optional; defaults to DEFAULT_MAX_SIGNATURES (100)
  // when absent so existing config.json files keep working unchanged.
  maxStoredSignatures?: number;
}

/**
 * Which Manual Checkout form a customer sees.
 * - 'txam'      — the default form (Associated Party, Foreman, Location,
 *                 picker name/company, signature).
 * - 'workOrder' — a single "Work Order # / Maintenance Document Number" field,
 *                 barcode-scannable, with none of the TXAM fields.
 */
export type ManualCheckoutFormVariant = 'txam' | 'workOrder';

export interface AppConfig {
  environment: 'development' | 'testing' | 'staging' | 'production';
  smartApi: ApiConfig;
  swsApi: ApiConfig;
  features: FeaturesConfig;
  // (2026-08-03) Per-company Manual Checkout form variant. Maps a Smart
  // companyId (string) to the checkout form that company should see. Absent or
  // unlisted companies get the default 'txam' form. Reason: TXAM (585) and
  // Profrac (588) are both industry 3, so the form must be chosen per company,
  // not per industry.
  manualCheckoutForms?: Record<string, ManualCheckoutFormVariant>;
}

export const DEFAULT_CONFIG: AppConfig = {
  environment: 'production',
  smartApi: {
    baseUrl: 'https://smart.reboundynamics.com/public',
    loginPath: '/base/v1/logins',
    useProxy: false,
    proxyPath: ''
  },
  swsApi: {
    baseUrl: 'https://api.reboundynamics.com/api/v1',
    loginPath: '/login',
    useProxy: false,
    proxyPath: ''
  },
  features: {
    enableOfflineMode: true,
    enableDebugLogging: false,
    maxStoredSignatures: 100
  },
  // (2026-08-03) Profrac-Customer (companyId 588) uses the Work Order form.
  // (2026-08-04) Only the customer account is mapped -- vendor accounts are
  // blocked from Manual Checkout entirely, so mapping 587 would be dead config.
  manualCheckoutForms: {
    '588': 'workOrder'
  }
};